import axios from "axios";
import {
  getToken,
  logout,
  refreshAccessToken,
} from "../common/routines/user-operations";
import store from "../store";
import { DOWNLOAD_HOST } from "../constants/axios";
import { jwtDecode } from "jwt-decode";

const setupAxios = () => {
  const appState = store.getState();

  const baseURL = `${appState.server.serverProtocol}://${appState.server.serverUrl}:${appState.server.serverPort}/api/v1`;

  const instance = axios.create({
    baseURL,
  });

  instance.interceptors.request.use(async (config) => {
    const customConfig = config;
    const appServerState = store.getState();
    const appBaseURL = `${appServerState.server.serverProtocol}://${appServerState.server.serverUrl}:${appServerState.server.serverPort}/api/v1`;

    customConfig.baseURL = appBaseURL;

    if (
      config.url !== "/login" &&
      config.url !== "/refresh-token" &&
      !config?.url?.includes(DOWNLOAD_HOST)
    ) {
      customConfig.headers.Authorization = await getToken();
      customConfig.headers.source = "AFH";
    }
    return customConfig;
  });

  let isRefreshing = false;
  let refreshSubscribers: ((token: string) => void)[] = [];

  const subscribeTokenRefresh = (callback: (token: string) => void) => {
    refreshSubscribers.push(callback);
  };

  const onRefreshed = (token: string) => {
    refreshSubscribers.forEach((callback) => callback(token));
    refreshSubscribers = [];
  };

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const isLoginRequest = originalRequest.url?.includes("/login");
      const isRefreshRequest = originalRequest.url?.includes("/refresh-token");

      if (
        error?.response?.status === 401 ||
        error?.response?.data?.message ===
          "Your role has been updated, please re-login to access the application"
      ) {
        localStorage.setItem(
          "roleNotification",
          "Your role has been updated, please re-login to access the application"
        );
        logout();
        // window.location.href = "/no-role";
      }

      if (
        error?.response?.status === 401 &&
        !originalRequest._retry &&
        !isLoginRequest &&
        !isRefreshRequest
      ) {
        if (
          error?.response?.data?.message !==
          "Your role has been updated, please re-login to access the application"
        ) {
          if (isRefreshing) {
            return new Promise((resolve) => {
              subscribeTokenRefresh((token) => {
                originalRequest.headers.Authorization = `${token}`;
                resolve(instance(originalRequest));
              });
            });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          try {
            console.warn("Fallback token refresh triggered due to 401");
            const newToken = await refreshAccessToken();
            localStorage.setItem("token", newToken);
            instance.defaults.headers.common["Authorization"] = `${newToken}`;
            originalRequest.headers.Authorization = `${newToken}`;
            onRefreshed(newToken);
            return instance(originalRequest);
          } catch (refreshError) {
            localStorage.setItem(
              "roleNotification",
              "Session expired, please login again"
            );
            logout();
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }
      }

      // if (error?.response?.status === 429) {
      //   logout();
      // }

      if (
        error?.response?.status === 403 ||
        error?.response?.data?.message ===
          "Role permissions updated, please log into the application again."
      ) {
        localStorage.setItem(
          "roleNotification",
          "Role permissions updated, please log into the application again."
        );
        // window.location.href = "/no-role";
        logout();
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

export default setupAxios;
